import { Redirect, useParams } from "react-router-dom"
import React, { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { Button, Icon, Modal, TextContainer } from "@shopify/polaris"
import { useTranslation } from "react-i18next"
import { InfoMinor } from "@shopify/polaris-icons"
import className from "classnames"
import ThemeTabs from "components/widgets/ThemeTabs"
import { OBJECTIVES_THEMES } from "utils/constants/pillars"
import { useCollectContext } from "./CollectContextProvider"
import { useAppContext } from "components/AppContext"
import AccessModal from "components/AccessModal"
import {
  IMPROVE_MATURITY_PAGE,
  IMPROVE_WEBSITES_PAGE,
} from "utils/constants/improve/pages"

/**
 * Paywall configuration per collect theme.
 * To gate a new module behind a subscription feature:
 *   1. Add an entry here with { objectiveId, themeId, featureKey, i18nKey }
 *   2. Add translation keys under common:subscription_feature_missing.{i18nKey}
 *      → description (with \n for line breaks)
 *      → mailTemplateSubject (with {{currentOrganizationName}})
 *      → mailTemplateBody (with {{currentUserFirstname}} {{currentUserLastname}})
 */
const THEME_PAYWALLS = [
  {
    objectiveId: "13",
    themeId: "ai_services",
    featureKey: "env_ai_services",
    i18nKey: "ai_services",
  },
]

const ThemesFrame = ({ pageName, children }) => {
  const { t } = useTranslation()
  const { subscription, currentOrganization, currentUser } = useAppContext()

  const { progress, isCollectLocked, collect } =
    pageName === "collect" ? useCollectContext() : {}

  const { pillarId, objectiveId, themeId } = useParams()
  const themes = OBJECTIVES_THEMES[objectiveId]
  const [isModalOpen, setModalOpen] = useState(false)

  const activePaywall =
    pageName === "collect"
      ? THEME_PAYWALLS.find(
          (p) =>
            p.objectiveId === objectiveId &&
            p.themeId === themeId &&
            !subscription?.features?.[p.featureKey]
        )
      : null

  const shouldScopePaywall = !!activePaywall

  const paywallTexts = activePaywall
    ? {
        description: t(
          `common:subscription_feature_missing.${activePaywall.i18nKey}.description`
        ),
        mailSubject: t(
          `common:subscription_feature_missing.${activePaywall.i18nKey}.mailTemplateSubject`,
          { currentOrganizationName: currentOrganization?.name || "" }
        ),
        mailBody: t(
          `common:subscription_feature_missing.${activePaywall.i18nKey}.mailTemplateBody`,
          {
            currentUserFirstname: currentUser?.firstname || "",
            currentUserLastname: currentUser?.lastname || "",
          }
        ),
      }
    : undefined

  const TextToFormat = (text) =>
    text.split("\n").map((value, index) => {
      return (
        <span key={index}>
          {value}
          <br />
        </span>
      )
    })

  const toggleModalOpen = useCallback(() => {
    setModalOpen(!isModalOpen)
  }, [isModalOpen])

  const modalMarkup = () => {
    return (
      <Modal
        open={isModalOpen}
        onClose={toggleModalOpen}
        title={t(
          `collect:pillars.${pillarId}.objectives.${objectiveId}.themes.${themeId}.title`
        )}
      >
        <Modal.Section>
          <div>
            <TextContainer>
              <p>
                {TextToFormat(
                  t(
                    `collect:pillars.${pillarId}.objectives.${objectiveId}.themes.${themeId}.description`
                  )
                )}
              </p>
            </TextContainer>
          </div>
        </Modal.Section>
      </Modal>
    )
  }

  const renderModalButton = (pageName) => {
    if (
      pageName === "analyze" ||
      pageName === IMPROVE_MATURITY_PAGE ||
      pageName === IMPROVE_WEBSITES_PAGE
    ) {
      return null
    }
    return (
      <div align="right" className={className(pillarId)}>
        <Button
          plain
          onClick={() => {
            toggleModalOpen()
          }}
        >
          <Icon source={InfoMinor} />
        </Button>
      </div>
    )
  }

  /**
   * @description Loads the theme tabs
   * When we click on an objective tab and the component loads for the first time,
   * themeId will be 'undefined' because we haven't clicked on a theme tab yet,
   * so we automatically redirect to the first theme tab.
   * Except for the improve page, because we want to be able to see a summary of recommendations per objective,
   * before clicking on a theme tab to see the detailed recommendations per theme.
   * @return a div containing the Theme tabs
   */
  const themeMarkup = () => {
    if (
      pageName !== "analyze" &&
      pageName !== IMPROVE_MATURITY_PAGE &&
      pageName !== IMPROVE_WEBSITES_PAGE &&
      !themes.includes(themeId)
    ) {
      // TODO: Dynamically load default theme
      return (
        <Redirect to={`/${pageName}/${pillarId}/${objectiveId}/${themes[0]}`} />
      )
    }
    return (
      <>
        <ThemeTabs
          pageName={pageName}
          pillar={pillarId}
          objective={objectiveId}
          theme={themeId}
          progress={isCollectLocked ? null : progress}
          collect={collect}
        />
        <div
          className={className("themesContent", {
            AccessModalScope: shouldScopePaywall,
          })}
        >
          {renderModalButton(pageName)}
          {children}
          <AccessModal open={shouldScopePaywall} scoped texts={paywallTexts} />
        </div>
      </>
    )
  }

  return (
    <>
      {modalMarkup()}
      {themeMarkup()}
    </>
  )
}

ThemesFrame.propTypes = {
  children: PropTypes.any.isRequired,
  pageName: PropTypes.string.isRequired,
}

export default ThemesFrame
